/**
 * Accounting > Budget. The multi-year budget: income, cost of goods, payroll by person,
 * expenses and cash flow.
 *
 * The numbers are one document on the server (budget.js, limited by name to Greg and
 * Mercedes because the Payroll tab lists everyone's pay). The drawing and arithmetic are
 * in public/budget-shell.html, which is the page that was first built as a Claude
 * artifact; it is shown here in a frame so it looks and behaves the same.
 *
 * The frame cannot sign requests itself, so when somebody fills in "Add details" on a
 * loan it posts a message here and this page uploads the files and saves the document.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { budgetDoc } from '../services/api';
import { getAccessToken, getCurrentUser } from '../services/auth';

// The shell is page content only (as it was published). This gives it a document.
const wrap = (shell, data) => `<!doctype html><html data-theme="light"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>html{color-scheme:light}body{margin:0;font-size:14px}img{max-width:100%}[hidden]{display:none!important}</style>
</head><body>${shell.replace('/*DATA*/', () => JSON.stringify(data).replace(/</g, '\\u003c'))}</body></html>`;

// Same presigned upload the Accounting Brain uses: straight to storage, no size cap.
async function uploadDoc(file) {
  const pres = await fetch('/.netlify/functions/r2-upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
    body: JSON.stringify({ action: 'presign-doc', filename: file.name, contentType: file.type || 'application/octet-stream', prefix: 'accounting' }),
  });
  const p = await pres.json().catch(() => ({}));
  if (!pres.ok || !p.uploadUrl) throw new Error(p.error || `Could not prepare the upload of ${file.name}.`);
  const put = await fetch(p.uploadUrl, { method: 'PUT', headers: { 'Content-Type': p.contentType || file.type || 'application/octet-stream' }, body: file });
  if (!put.ok) throw new Error(`Upload of ${file.name} failed (${put.status}).`);
  return { name: file.name, key: p.key, size: file.size };
}

export default function Budget() {
  const [html, setHtml] = useState('');
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState('');
  const [height, setHeight] = useState(900);
  const frame = useRef(null);
  const shellText = useRef('');

  const draw = useCallback((doc) => { setMeta(doc); setHtml(wrap(shellText.current, { ...doc.data, updated: doc.updated_on })); }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [doc, res] = await Promise.all([budgetDoc('get'), fetch(`${process.env.PUBLIC_URL || ''}/budget-shell.html`)]);
        if (!res.ok) throw new Error('Could not load the budget page.');
        const shell = await res.text();
        if (!live) return;
        shellText.current = shell;
        draw(doc);
      } catch (e) { if (live) setErr(e.message || 'Could not load the budget.'); }
    })();
    return () => { live = false; };
  }, [draw]);

  // "Add details" on a loan. Files go to storage first, then the loan in the document
  // gets what was sent. A save replaces the whole document, so it is always built on a
  // fresh copy, and tried once more if somebody else saved in between.
  useEffect(() => {
    const onMsg = async (e) => {
      if (!frame.current || e.source !== frame.current.contentWindow) return;
      const m = e.data || {};
      const post = (x) => { try { e.source.postMessage(x, '*'); } catch (_) { /* frame already redrawn */ } };
      // "See the transactions": read or mark the charges behind a line. These do not touch
      // the document, so the frame is not redrawn and the overlay stays open.
      if (m.type === 'budget-txns') {
        try { post({ type: 'budget-txns-data', line: m.line, ...(await budgetDoc('txns', { line: m.line })) }); }
        catch (ex) { post({ type: 'budget-txns-data', line: m.line, error: ex.message || 'Could not load the transactions.' }); }
        return;
      }
      if (m.type === 'budget-txn-mark') {
        try { post({ type: 'budget-txn-marked', ...(await budgetDoc('txn-mark', { id: m.id, treatment: m.treatment, reason: m.reason })) }); }
        catch (ex) { post({ type: 'budget-txn-marked', id: m.id, error: ex.message || 'Could not save that.' }); }
        return;
      }
      if (m.type !== 'budget-submit' || !['loan', 'line', 'approve', 'final'].includes(m.kind)) return;
      const reply = (x) => post({ type: 'budget-submit-done', ...x });
      try {
        const files = [];
        for (const file of (m.files || [])) files.push(await uploadDoc(file));
        const on = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const f = m.fields || {};
        const u = getCurrentUser() || {};
        const by = u.staffName || u.name || u.username || '';
        for (let attempt = 0; ; attempt++) {
          const doc = await budgetDoc('get');
          if (m.kind === 'loan') {
            const loan = ((doc.data.cash || {}).loans || []).find((l) => (l.company || 'cp') === m.company && l.name === m.name);
            if (!loan) throw new Error('That loan is no longer on the page. Reload and try again.');
            loan.submitted = [...(loan.submitted || []), { on, by, ...f, files }];
            loan.needsClaude = true;
            if (f.maturity) loan.maturity = `${f.maturity} (entered ${on}, not yet checked)`;
          } else {
            const line = (doc.data.expenseLines || []).find((l) => l.name === m.name);
            if (!line) throw new Error('That line is no longer on the page. Reload and try again.');
            if (m.kind === 'line') {
              line.submitted = [...(line.submitted || []), { on, by, answers: m.answers || [], note: m.note || '', files }];
              line.needsClaude = true;
            } else if (m.kind === 'final') {
              // The final pass (Greg, Matt and Mercedes), separate from the first approval.
              if (m.done) line.finalReview = { on, by, who: String(m.who || '').slice(0, 200) };
              else delete line.finalReview;
            } else if (m.approved) {
              line.approved = true; line.approvedBy = by; line.approvedOn = on;
            } else {
              line.approved = false; delete line.approvedBy; delete line.approvedOn;
            }
          }
          try { await budgetDoc('save', { data: doc.data, version: doc.version }); break; }
          catch (saveErr) { if (attempt >= 1 || saveErr.status !== 409) throw saveErr; }
        }
        reply({ ok: true });
        draw(await budgetDoc('get'));
      } catch (ex) { reply({ ok: false, error: ex.message || 'Could not save. Try again.' }); }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [draw]);

  // Size the frame to its content so the page scrolls as one, and follow tab changes.
  const onLoad = () => {
    const doc = frame.current && frame.current.contentDocument;
    if (!doc) return;
    const fit = () => setHeight(Math.max(600, doc.documentElement.scrollHeight));
    fit();
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(fit).observe(doc.body);
    doc.addEventListener('click', () => setTimeout(fit, 0));
  };

  if (err) return <div style={{ padding: 24 }}><div style={{ background: '#fdeaea', color: '#b42318', padding: '10px 14px', borderRadius: 6, maxWidth: 640 }}>{err}</div></div>;
  if (!html) return <div style={{ padding: 24 }}>Loading the budget…</div>;
  return (
    <div>
      <iframe ref={frame} title="Budgets" srcDoc={html} onLoad={onLoad}
        style={{ width: '100%', height, border: 0, display: 'block' }} />
      {meta && <p style={{ margin: '0 24px 24px', fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
        Saved {meta.updated_on}{meta.updated_by ? ` by ${meta.updated_by}` : ''}. Version {meta.version}.
      </p>}
    </div>
  );
}
